import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import InfoTooltipButton from "./InfoTooltipButton";
import Link from "@material-ui/core/Link";
import Box from "@material-ui/core/Box";

import { useGlobal } from "../../GlobalContext.js";
import defaultImage from "assets/img/default_icon.png";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import CardHeader from "../../components/Card/CardHeader";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "../../components/Card/CardFooter";
import CardText from "../../components/Card/CardText";

import { useTranslation } from "react-i18next";

/**
 * Mapa de tooltips permitidos para esta card.
 */
const HEADER_INFO = {
  infoapp: "metainfo-tooltip-infoapp",
};

const useStyles = makeStyles((theme) => ({
  /**
   * La pastilla visual real del header es CardText.
   * Forzamos que el contenido interno viva ahí.
   */
  headerBadge: {
    display: "block",
    width: "fit-content",
    maxWidth: "100%",
  },

  /**
   * Cabecera responsive dentro de la propia pastilla turquesa.
   */
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
    maxWidth: "100%",
  },

  headerTitleWrap: {
    minWidth: 0,
    flex: 1,
  },

  cardTitle: {
    margin: 0,
    color: "#fff",
    fontWeight: 400,
    fontSize: "1rem",
    wordBreak: "break-word",

    [theme.breakpoints.up("md")]: {
      fontSize: "1.2rem",
    },
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },

  /**
   * El icono ya no depende del fondo transparente de CardHeader.
   * Le damos además un soporte visual propio.
   */
  infoIconWrapper: {
    width: 30,
    height: 30,
    padding: 0,
    color: "#fff",
    backgroundColor: "rgba(255, 255, 255, 0.16)",
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.24)",
    },
  },

  infoIcon: {
    width: 18,
    height: 18,
    fontSize: 18,
    display: "block",
    position: "relative",
    top: "-2px",
  },

  tooltip: {
    fontSize: "0.9rem",
    padding: "12px 16px",
    maxWidth: 320,
  },

  arrow: {},

  /**
   * Body general de la card.
   */
  body: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },

  /**
   * Bloque superior: icono + descripción.
   */
  topSection: {
    marginBottom: theme.spacing(2),
  },

  iconWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",

    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(1),
    },
  },

  /**
   * Ajuste principal:
   * en anchos tipo tablet/1024 el icono no debe quedar enano.
   */
  appIcon: {
    width: "100%",
    maxWidth: 160,
    height: "auto",
    objectFit: "contain",

    [theme.breakpoints.down("md")]: {
      maxWidth: 148,
    },

    [theme.breakpoints.down("sm")]: {
      maxWidth: 108,
    },

    [theme.breakpoints.down("xs")]: {
      maxWidth: 84,
    },
  },

  description: {
    wordBreak: "break-word",
    whiteSpace: "pre-line",
    lineHeight: 1.6,
    textAlign: "left",

    [theme.breakpoints.down("sm")]: {
      textAlign: "left",
    },
  },

  /**
   * Lista de metadatos en formato limpio y responsive.
   */
  detailsSection: {
    marginTop: theme.spacing(1),
  },

  detailRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
    borderBottom: "1px solid rgba(0,0,0,0.08)",

    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      gap: theme.spacing(0.25),
    },
  },

  detailLabel: {
    fontWeight: 600,
    minWidth: 170,
    flexShrink: 0,
    wordBreak: "break-word",

    [theme.breakpoints.down("sm")]: {
      minWidth: 140,
    },

    [theme.breakpoints.down("xs")]: {
      minWidth: "unset",
    },
  },

  detailValue: {
    minWidth: 0,
    flex: 1,
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },

  emptyState: {
    padding: theme.spacing(2, 0),
    textAlign: "center",
    wordBreak: "break-word",
  },
}));

/**
 * Trunca la descripción sin romper la UX.
 */
const truncateDescription = (value, maxLength = 500) => {
  if (!value) return "";
  return value.length > maxLength ? `${value.substring(0, maxLength)}...` : value;
};

/**
 * Convierte valores vacíos en fallback legible.
 */
const getSafeValue = (value, fallback = "-") => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string" && value.trim() === "") return fallback;
  return value;
};

const MetaInfo = ({ infoKey }) => {
  const classes = useStyles();
  const { t } = useTranslation("search");
  const { sharedValue } = useGlobal();

  /**
   * Buscamos la APK seleccionada a partir del packageName actual.
   * Así el componente reacciona también cuando cambia la selección.
   */
  const apkData = Array.isArray(sharedValue?.apkData) ? sharedValue.apkData : [];
  const packageName = sharedValue?.packageName || "";

  const record = apkData.find((item) => item?.package === packageName) || null;

  const tooltipKey = infoKey && HEADER_INFO[infoKey] ? HEADER_INFO[infoKey] : null;
  const tooltipText = tooltipKey ? t(tooltipKey, { defaultValue: "" }) : "";

  if (!record) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="body1" color="textSecondary">
          {t("metainfo-no-records")}
        </Typography>
      </Box>
    );
  }

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary" icon>
            <CardText color="primary" className={classes.headerBadge}>
              <div className={classes.headerContent}>
                <div className={classes.headerTitleWrap}>
                  <h4 className={classes.cardTitle}>{t("metainfo-card-title")}</h4>
                </div>

                {tooltipText ? (
                  <div className={classes.headerActions}>
                    <InfoTooltipButton
                      title={tooltipText}
                      placement="right"
                      tooltipClasses={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                      iconButtonClassName={classes.infoIconWrapper}
                      iconClassName={classes.infoIcon}
                    />
                  </div>
                ) : null}
              </div>
            </CardText>
          </CardHeader>

          <CardBody className={classes.body}>
            <GridContainer className={classes.topSection} spacing={3} alignItems="center">
              <GridItem xs={12} sm={4} md={3} className={classes.iconWrap}>
                <img
                  src={record.iconUrl || defaultImage}
                  alt={t("metainfo-icon-alt")}
                  className={classes.appIcon}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = defaultImage;
                  }}
                />
              </GridItem>

              <GridItem xs={12} sm={8} md={9}>
                <Typography variant="body2" className={classes.description}>
                  {truncateDescription(record.description)}
                </Typography>
              </GridItem>
            </GridContainer>

            <div className={classes.detailsSection}>
              <div className={classes.detailRow}>
                <Typography variant="body2" className={classes.detailLabel}>
                  {t("metainfo-label-ratings")}
                </Typography>
                <Typography variant="body2" className={classes.detailValue}>
                  {getSafeValue(record.ratings)}
                </Typography>
              </div>

              <div className={classes.detailRow}>
                <Typography variant="body2" className={classes.detailLabel}>
                  {t("metainfo-label-installs")}
                </Typography>
                <Typography variant="body2" className={classes.detailValue}>
                  {getSafeValue(record.realInstalls)}
                </Typography>
              </div>

              <div className={classes.detailRow}>
                <Typography variant="body2" className={classes.detailLabel}>
                  {t("metainfo-label-reviews")}
                </Typography>
                <Typography variant="body2" className={classes.detailValue}>
                  {getSafeValue(record.reviews)}
                </Typography>
              </div>

              <div className={classes.detailRow}>
                <Typography variant="body2" className={classes.detailLabel}>
                  {t("metainfo-label-score")}
                </Typography>
                <Typography variant="body2" className={classes.detailValue}>
                  {getSafeValue(record.score)}
                </Typography>
              </div>

              <div className={classes.detailRow}>
                <Typography variant="body2" className={classes.detailLabel}>
                  {t("metainfo-label-developer")}
                </Typography>
                <Typography variant="body2" className={classes.detailValue}>
                  {getSafeValue(record.developer)}
                </Typography>
              </div>

              <div className={classes.detailRow}>
                <Typography variant="body2" className={classes.detailLabel}>
                  {t("metainfo-label-developer-email")}
                </Typography>
                <Typography variant="body2" className={classes.detailValue}>
                  {getSafeValue(record.developerEmail)}
                </Typography>
              </div>

              <div className={classes.detailRow}>
                <Typography variant="body2" className={classes.detailLabel}>
                  {t("metainfo-label-developer-url")}
                </Typography>

                <div className={classes.detailValue}>
                  {record.developerWebsiteUrl ? (
                    <Link
                      href={record.developerWebsiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {record.developerWebsiteUrl}
                    </Link>
                  ) : (
                    <Typography variant="body2">
                      {getSafeValue(record.developerWebsiteUrl)}
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          </CardBody>

          <CardFooter />
        </Card>
      </GridItem>
    </GridContainer>
  );
};

MetaInfo.propTypes = {
  infoKey: PropTypes.string,
};

export default MetaInfo;